import { useEffect, useMemo, useState } from 'react';
import { featuredArticlesApi, pageContentApi } from './api';
import { slugify } from './slugify';

// Articles mirrors How Does It Work?'s Section -> Article drill-down (see
// howItWorksHierarchy.js). The admin-managed `featured_articles` cards are the catalog:
// each card's `category` is its section (e.g. "Process", "ECUs") and its admin `order`
// sets the order within that section. There is no stored section table — a section
// exists only because at least one card carries that category string.
export const TOP_ARTICLES_N = 6;

const bareSlugFromPath = (path) => (path || '').replace(/^\/articles\/(reference\/)?/, '').replace(/^article-/, '');
const bareSlugFromPcSlug = (slug) => (slug || '').replace(/^article-/, '');

// Fetches the section catalog once. The page_contents article list is fetched only to
// attach each card's `page_content_id`, which is what favorites are keyed by for
// DB-backed articles — the cards themselves carry no stable content id.
export const useArticleSections = () => {
  const [featured, setFeatured] = useState([]);
  const [reference, setReference] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      featuredArticlesApi.getFeaturedArticles(),
      pageContentApi.getPublicArticleList().catch(() => []),
    ])
      .then(([f, r]) => {
        if (cancelled) return;
        setFeatured(f);
        setReference(r);
      })
      .catch((err) => {
        console.error('Error fetching articles:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const articles = useMemo(() => {
    const pcIdBySlug = new Map(reference.map((r) => [bareSlugFromPcSlug(r.slug), r.page_content_id]));
    return featured
      .filter((a) => a.path)
      .map((a) => ({ ...a, page_content_id: pcIdBySlug.get(bareSlugFromPath(a.path)) || null }));
  }, [featured, reference]);

  return { articles, loading };
};

export const sectionSlugOf = (article) => slugify(article.category || 'Other');

// [section name, articles[]] entries, preserving first-appearance order (the API already
// returns cards in admin order). Cards with no category fall into a trailing "Other".
export const groupArticlesBySection = (articles) => {
  const groups = new Map();
  articles.forEach((article) => {
    const section = article.category || 'Other';
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section).push(article);
  });
  return Array.from(groups.entries());
};

export const articlesInSection = (articles, sectionSlug) =>
  articles.filter((a) => sectionSlugOf(a) === sectionSlug);

export const resolveSectionName = (articles, sectionSlug) => {
  const match = articles.find((a) => sectionSlugOf(a) === sectionSlug);
  return match ? match.category || 'Other' : null;
};

// Shared shape consumed by <TrendingSection>/<TrendingCard>. The id is the card's own
// path, which is also what PageVisitTracker records visits under.
export const articleToTrendingItem = (article) => ({
  id: article.path,
  to: article.path,
  title: article.title,
  description: article.description,
  badge: article.category,
  meta: article.reading_time,
  updatedAt: article.updated_at,
  ...(article.page_content_id
    ? { contentType: 'page_content_article', contentId: article.page_content_id }
    : {}),
});
